import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  LinearProgress,
  Alert,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { BRAND_COLORS, alpha } from '../styles/theme';
import { useUploadDocument } from './Documents.mutations';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useTranslation } from '../i18n';

interface DocumentUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const DocumentUploadDialog: React.FC<DocumentUploadDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { showSuccess } = useErrorHandler();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [documentType, setDocumentType] = useState('other');
  const [validityStartDate, setValidityStartDate] = useState('');
  const [validityEndDate, setValidityEndDate] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const uploadDocument = useUploadDocument();
  const loading = uploadDocument.isPending;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Auto-fill title if empty
      if (!title) {
        setTitle(file.name.replace(/\.[^/.]+$/, '')); // Remove extension
      }
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError(t('documents.titleRequired'));
      return;
    }

    if (!selectedFile) {
      setError(t('documents.pleaseSelectFile'));
      return;
    }

    // Validate dates if provided
    if (validityStartDate && validityEndDate) {
      const start = new Date(validityStartDate);
      const end = new Date(validityEndDate);
      if (start > end) {
        setError(t('documents.startDateBeforeEnd'));
        return;
      }
    }

    setError(null);

    try {
      await uploadDocument.mutateAsync({
        file: selectedFile,
        title: title.trim(),
        description: description.trim() || null,
        documentType,
        validityStartDate: validityStartDate || null,
        validityEndDate: validityEndDate || null,
      });
      // Reset form
      setTitle('');
      setDescription('');
      setDocumentType('other');
      setValidityStartDate('');
      setValidityEndDate('');
      setSelectedFile(null);
      showSuccess(t('documents.uploadSuccess'));
      onSuccess();
    } catch {
      // Toast (with server detail) is raised by the global mutation handler;
      // the inline alert stays a stable, user-friendly retry prompt.
      setError(t('documents.uploadFailed'));
    }
  };

  const handleClose = () => {
    if (!loading) {
      setTitle('');
      setDescription('');
      setDocumentType('other');
      setValidityStartDate('');
      setValidityEndDate('');
      setSelectedFile(null);
      setError(null);
      uploadDocument.reset();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('documents.uploadDocument')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* File Upload */}
          <Box>
            <input
              accept="*/*"
              style={{ display: 'none' }}
              id="document-file-upload"
              type="file"
              onChange={handleFileSelect}
              disabled={loading}
            />
            <label htmlFor="document-file-upload">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                startIcon={<CloudUploadIcon />}
                disabled={loading}
                sx={{
                  py: 2,
                  borderStyle: 'dashed',
                  borderColor: selectedFile
                    ? BRAND_COLORS.forestGreen.main
                    : alpha(BRAND_COLORS.forestGreen.main, 0.3),
                  '&:hover': {
                    borderColor: BRAND_COLORS.forestGreen.main,
                    backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.05),
                  },
                }}
              >
                {selectedFile ? selectedFile.name : t('documents.selectFile')}
              </Button>
            </label>
          </Box>

          {/* Title */}
          <TextField
            label={t('documents.titleLabel')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            disabled={loading}
          />

          {/* Description */}
          <TextField
            label={t('documents.descriptionLabel')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            disabled={loading}
          />

          {/* Document Type */}
          <FormControl fullWidth disabled={loading}>
            <InputLabel>{t('documents.documentType')}</InputLabel>
            <Select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              label={t('documents.documentType')}
            >
              <MenuItem value="insurance">{t('documents.typeInsurance')}</MenuItem>
              <MenuItem value="permit">{t('documents.typePermit')}</MenuItem>
              <MenuItem value="other">{t('documents.typeOther')}</MenuItem>
            </Select>
          </FormControl>

          {/* Validity Dates */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('documents.validityStartDate')}
              type="date"
              value={validityStartDate}
              onChange={(e) => setValidityStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={loading}
            />
            <TextField
              label={t('documents.validityEndDate')}
              type="date"
              value={validityEndDate}
              onChange={(e) => setValidityEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={loading}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !title.trim() || !selectedFile}
          sx={{
            backgroundColor: BRAND_COLORS.forestGreen.main,
            '&:hover': {
              backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.8),
            },
          }}
        >
          {t('common.upload')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentUploadDialog;
